import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import OfferingNotice from '../../components/OfferingNotice';
import PageHeader from '../../components/layout/PageHeader';
import ClientMessageBanner from '../../components/contracts/ClientMessageBanner';
import ContractSigningWidget from '../../components/contracts/ContractSigningWidget';
import AssistanceModal from '../../components/contracts/AssistanceModal';
import ReviewNextSteps from '../../components/client/ReviewNextSteps';
import KeyTermsSummary from '../../components/contracts/KeyTermsSummary';
import contractsService from '../../services/contracts.service';
import commitmentsService from '../../services/commitments.service';
import ContractCommitmentCard from '../../components/commitments/ContractCommitmentCard';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';
import { STATE_BADGE } from '../../types/contracts';
import type { AssistanceType, CaseSummary, ContractCaseState } from '../../types/contracts';
import type { Commitment } from '../../types/commitments';
import { readerLocale } from '../../i18n/language';

const REVIEW_IN_PROGRESS_STATES: ReadonlySet<ContractCaseState> = new Set([
  'uploaded',
  'pending_mgr',
  'returned_to_agent',
] as const);

export default function ContractCase(): JSX.Element {
  const { caseId } = useParams<{ caseId: string }>();
  const [caseData, setCaseData] = useState<CaseSummary | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [assistanceOpen, setAssistanceOpen] = useState<AssistanceType | null>(null);
  const [assistanceConfirmation, setAssistanceConfirmation] = useState<string | null>(null);
  const [commitment, setCommitment] = useState<Commitment | null>(null);

  const loadCase = useCallback(async () => {
    if (!caseId) return;
    try {
      const data = await contractsService.getCase(caseId);
      setCaseData(data);
      setLoadError(null);
    } catch (err: unknown) {
      setLoadError(getApiErrorMessage(err, 'Could not load this contract case.'));
    }
  }, [caseId]);

  useEffect(() => {
    loadCase();
  }, [loadCase]);

  // A commitment contract carries a matching commitment in the client's own
  // list (linked by contract_case_id). Surface it reciprocally so a member who
  // just signed sees their commitment without navigating to Portfolio.
  // Best-effort: a failure just hides the card (the contract page still works).
  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    commitmentsService
      .listMine()
      .then((list) => {
        if (!cancelled) setCommitment(list.find((c) => c.contract_case_id === caseId) ?? null);
      })
      .catch(() => { /* non-blocking — card just won't render */ });
    return () => { cancelled = true; };
  }, [caseId]);

  async function handleDownload(): Promise<void> {
    if (!caseId) return;
    setDownloadError(null);
    setDownloading(true);
    try {
      const { download_url } = await contractsService.getDownloadUrl(caseId);
      // Open in a new tab. The presigned URL is short-lived (15 min); a fresh
      // one is generated on every click so the user can retry safely.
      window.open(download_url, '_blank', 'noopener,noreferrer');
      // Refresh case so rendered_at populates after the lazy first render.
      await loadCase();
    } catch (err: unknown) {
      setDownloadError(getApiErrorMessage(err, 'Could not generate the download link.'));
    } finally {
      setDownloading(false);
    }
  }

  function handleAssistanceSubmitted(): void {
    setAssistanceConfirmation(
      'Got it — your request was sent to your Account Executive. You can keep working or come back later.',
    );
    setAssistanceOpen(null);
    loadCase();
  }

  if (!caseId) {
    return (
      <p className="text-sm" style={{ color: colors.muted }}>
        Missing case identifier.
      </p>
    );
  }

  if (loadError) {
    return (
      <>
        <PageHeader title="Contract" />
        <div
          className="rounded-2xl p-6"
          role="alert"
          style={{
            background: colors.dangerBg,
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
          }}
        >
          {loadError}
        </div>
        <div className="mt-4">
          <Link to="/client/contracts" className="text-sm" style={{ color: colors.ink }}>
            ← Back to contracts
          </Link>
        </div>
      </>
    );
  }

  if (!caseData) {
    return (
      <>
        <PageHeader title="Contract" />
        <p className="text-sm" style={{ color: colors.muted }}>
          Loading…
        </p>
      </>
    );
  }

  const badge = STATE_BADGE[caseData.state_slug];
  const showUploadCard = caseData.can_upload;
  const isReUpload = caseData.state_slug === 'returned_to_client';
  const reviewInProgress = REVIEW_IN_PROGRESS_STATES.has(caseData.state_slug);
  const isFinal = caseData.state_slug === 'approved' || caseData.state_slug === 'rejected';

  return (
    <>
      <PageHeader
        title={caseData.template_title}
        subtitle={
          <span
            className="inline-block px-2 py-0.5 rounded text-xs font-medium"
            style={{ background: badge.bg, color: badge.fg }}
          >
            {badge.label}
          </span>
        }
        actions={
          <Link
            to="/client/contracts"
            className="text-sm"
            style={{ color: colors.muted }}
          >
            ← All contracts
          </Link>
        }
      />

      {assistanceConfirmation !== null && (
        <output
          className="rounded-lg p-4 mb-6 text-sm flex items-start justify-between gap-3"
          style={{
            background: colors.successBg,
            color: colors.success,
            border: '1px solid #A7F3D0',
            display: 'flex',
          }}
        >
          <span>{assistanceConfirmation}</span>
          <button
            type="button"
            onClick={() => setAssistanceConfirmation(null)}
            className="text-xs underline flex-shrink-0"
            style={{ color: colors.success }}
          >
            Dismiss
          </button>
        </output>
      )}

      {isReUpload && caseData.client_message && (
        <ClientMessageBanner
          message={caseData.client_message}
          onAwaitContact={() => setAssistanceOpen('await_contact')}
          onScheduleCall={() => setAssistanceOpen('schedule_call')}
        />
      )}

      {/* Approved / Rejected — final summary card */}
      {isFinal && (
        <div
          className="rounded-2xl p-6 mb-6"
          style={{
            background: caseData.state_slug === 'approved' ? colors.successBg : colors.dangerBg,
            border: `1px solid ${
              caseData.state_slug === 'approved' ? '#A7F3D0' : colors.dangerBorder
            }`,
          }}
        >
          <h2
            className="text-base font-semibold mb-1"
            style={{ color: caseData.state_slug === 'approved' ? colors.success : colors.danger }}
          >
            {caseData.state_slug === 'approved'
              ? 'Your contract has been approved.'
              : 'Your contract was not approved.'}
          </h2>
          <p className="text-sm" style={{ color: colors.inkSoft }}>
            {caseData.state_slug === 'approved'
              ? 'Your agreement is active and a copy of your signed agreement is on file.'
              : 'Please contact your Account Executive if you need clarification or to start a new agreement.'}
          </p>
          {caseData.state_slug === 'approved' && !commitment && (
            <a
              href="/client/fund"
              className="inline-block mt-3 text-sm font-semibold"
              style={{ color: colors.ink }}
            >
              Contribute to this series →
            </a>
          )}
        </div>
      )}

      {/* Under-review notice */}
      {reviewInProgress && (
        <div
          className="rounded-2xl p-5 mb-6"
          style={{ background: '#EEF2FF', border: `1px solid #C7D2FE` }}
        >
          <p className="text-sm" style={{ color: '#3730A3' }}>
            Your contract is being reviewed. We&apos;ll notify you when there&apos;s an update — no
            action is needed right now.
          </p>
          <ReviewNextSteps
            paragraphs={[
              'Once your signed contract is approved, it appears in your Summary and you can make your first contribution right away.',
              'If our team needs a correction, this page will show the specifics and let you re-sign and resubmit.',
              'Your Account Executive can walk you through anything before then.',
            ]}
            cta={
              <div
                className="flex items-center justify-between gap-4 rounded-xl px-4 py-3 flex-wrap"
                style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
              >
                <p className="text-sm" style={{ color: colors.inkSoft }}>
                  Have questions while you wait?
                </p>
                <button
                  type="button"
                  onClick={() => setAssistanceOpen('await_contact')}
                  className="flex-shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                  style={{ background: colors.surface, color: colors.goldOnLight, border: `1px solid ${colors.goldOnLight}` }}
                >
                  Message your Account Executive →
                </button>
              </div>
            }
          />
        </div>
      )}

      {commitment && <ContractCommitmentCard commitment={commitment} />}

      {/* Personalized download card */}
      <section
        className="rounded-2xl p-6 mb-6"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <h2 className="text-base font-semibold mb-1" style={{ color: colors.ink }}>
          Your contract
        </h2>
        <p className="text-sm mb-3" style={{ color: colors.muted }}>
          A personalized PDF is generated with your information already filled in. Review it
          on screen, download a copy if you like, and sign it below.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            style={{
              background: colors.navy,
              color: '#FFFFFF',
              opacity: downloading ? 0.6 : 1,
              cursor: downloading ? 'wait' : 'pointer',
            }}
          >
            {downloading ? 'Preparing…' : 'Download PDF'}
          </button>
          {caseData.rendered_at && (
            <span className="text-xs" style={{ color: colors.mutedSoft }}>
              Last generated {formatDateTime(caseData.rendered_at)}
            </span>
          )}
        </div>
        {downloadError && (
          <div
            className="mt-3 rounded-lg p-3 text-sm"
            style={{
              background: colors.dangerBg,
              color: colors.danger,
              border: `1px solid ${colors.dangerBorder}`,
            }}
            role="alert"
          >
            {downloadError}
          </div>
        )}
      </section>

      {/* Sign card — e-signature (server-side PDF stamp, no file upload required) */}
      {showUploadCard && (
        <section
          className="rounded-2xl p-6"
          style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <h2 className="text-base font-semibold mb-1" style={{ color: colors.ink }}>
            {isReUpload ? 'Re-sign corrected contract' : 'Sign your contract'}
          </h2>
          <p className="text-sm mb-4" style={{ color: colors.muted }}>
            {isReUpload
              ? 'Review the corrected document below and sign it electronically to resubmit.'
              : 'Review the document below carefully, then sign it electronically. No file upload needed.'}
          </p>
          {caseData.product_terms && (
            <KeyTermsSummary terms={caseData.product_terms} />
          )}
          <KeyTermsAccordion />
          <ContractSigningWidget
            caseId={caseId}
            isReUpload={isReUpload}
            defaultSignerName={caseData.signer_name}
            onSignComplete={() => loadCase()}
          />
          {caseData.latest_signed_at !== null && (
            <p className="text-xs mt-3" style={{ color: colors.mutedSoft }}>
              Last signature received {formatDateTime(caseData.latest_signed_at)}.
            </p>
          )}
        </section>
      )}

      {assistanceOpen && (
        <AssistanceModal
          caseId={caseId}
          initialType={assistanceOpen}
          onClose={() => setAssistanceOpen(null)}
          onSubmitted={handleAssistanceSubmitted}
        />
      )}
      <OfferingNotice />
    </>
  );
}

function KeyTermsAccordion(): JSX.Element {
  return (
    <details
      style={{
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        marginBottom: 20,
        background: '#FAFBFF',
      }}
    >
      <summary
        style={{
          padding: '12px 16px',
          cursor: 'pointer',
          fontWeight: 600,
          fontSize: 13,
          color: colors.ink,
          listStyle: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>Key terms — read before signing</span>
        <span style={{ fontSize: 16, color: colors.muted }}>›</span>
      </summary>
      <div style={{ padding: '0 16px 16px', fontSize: 13, color: colors.inkSoft, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <li>
            <strong>Electronic signature.</strong> By confirming, you sign this contract
            electronically in the portal. Your identity, the time and the exact document are
            recorded.
          </li>
          <li>
            <strong>Document retention.</strong> A copy of your signed contract is stored
            securely.
          </li>
          <li>
            <strong>Accuracy of information.</strong> By signing, you confirm that all data
            in the personalized PDF is accurate and complete. Discrepancies must be reported
            to your Account Executive before signing.
          </li>
        </ul>
        <p style={{ margin: 0, fontSize: 12, color: colors.muted }}>
          Read the full PDF before signing. The PDF is the authoritative version of your
          contract.
        </p>
      </div>
    </details>
  );
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(readerLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
